/**
 * Capacitor Mobile App Configuration
 * This file initializes Capacitor plugins and handles mobile-specific functionality
 */

import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

// Check if running on a native platform
export const isNativePlatform = Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Initialize Capacitor plugins for mobile app
 */
export const initializeCapacitor = async () => {
    if (!isNativePlatform) {
        console.log('Running in web browser - skipping native initialization');
        return;
    }

    console.log(`Initializing Capacitor on ${getPlatform()}`);

    try {
        // Configure Status Bar
        await StatusBar.setStyle({ style: Style.Dark });

        if (getPlatform() === 'android') {
            await StatusBar.setBackgroundColor({ color: '#1a1a2e' });
        }

        // Hide splash screen after app is ready
        await SplashScreen.hide();

        // Set up keyboard listeners for better UX
        Keyboard.addListener('keyboardWillShow', (info) => {
            console.log('Keyboard will show with height:', info.keyboardHeight);
            document.body.classList.add('keyboard-visible');
        });

        Keyboard.addListener('keyboardWillHide', () => {
            console.log('Keyboard will hide');
            document.body.classList.remove('keyboard-visible');
        });

        // Set up app lifecycle listeners
        CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            console.log('App state changed. Is active:', isActive);
        });

        // Handle back button on Android
        CapacitorApp.addListener('backButton', ({ canGoBack }) => {
            if (!canGoBack) {
                CapacitorApp.exitApp();
            } else {
                window.history.back();
            }
        });

        // Handle deep links
        CapacitorApp.addListener('appUrlOpen', (event) => {
            console.log('App opened with URL:', event.url);
            // Handle deep link routing here if needed
        });

        console.log('Capacitor initialization complete');
    } catch (error) {
        console.error('Error initializing Capacitor:', error);
    }
};

/**
 * Get safe area insets for notched devices
 */
export const getSafeArea = () => {
    const safeArea = {
        top: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0', 10),
        bottom: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0', 10),
        left: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sal') || '0', 10),
        right: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sar') || '0', 10),
    };
    return safeArea;
};
